import React, { useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

/**
 * AreaChart Component - Plausible.io Style
 *
 * @param {Array} labels       - x-axis labels (dates or hours) for the current period
 * @param {Array} values       - y-axis values for the current period
 * @param {Array} prevValues   - optional y-axis values for the COMPARISON period.
 *                                When supplied, a second faded line + dashed stroke
 *                                renders on top of the main series — Plausible's
 *                                period-over-period overlay (image 1 in the spec).
 *                                Both series share the current period's x-axis;
 *                                position-N of prev is interpreted as "the matching
 *                                position in the comparison window" (so day 1 of
 *                                "Last 30 days" lines up with day 1 of "Previous 30 days").
 * @param {String} label       - Dataset label shown in the tooltip header
 * @param {String} period      - Time period (for label formatting heuristics)
 * @param {String} color       - Main color (hex) — comparison series derives a
 *                                slate variant from this automatically
 */

// Helper function to format seconds into "X min Y sec" format
const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || isNaN(seconds)) return '0 sec';
  
  const totalSeconds = Math.round(Number(seconds));
  
  if (totalSeconds === 0) return '0 sec';
  
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  
  if (minutes === 0) {
    return `${remainingSeconds} sec`;
  } else if (remainingSeconds === 0) {
    return `${minutes} min`;
  } else {
    return `${minutes} min ${remainingSeconds} sec`;
  }
};

const AreaChart = ({ labels = [], values = [], prevValues = null, prevLabels = null, label = 'Visitors', color = '#5046e4', period = '30d' }) => {
  // Whether the caller asked for the comparison overlay. `null` (default) means
  // no comparison; an empty array still counts as enabled (renders a flat line at 0).
  const hasComparison = Array.isArray(prevValues);
  const chartRef = useRef(null);
  
  // Check if we should display hourly format
  const isHourlyFormat = period === 'today' || period === '24h' || period === 'yesterday';
  
  // Format labels for display
  const formatLabel = (label) => {
    if (!label || typeof label !== 'string') return '';
    
    try {
      if (isHourlyFormat && label.includes(':')) {
        // Hourly format: "2024-11-27 14:00" -> "2pm"
        const parts = label.split(' ');
        if (parts.length < 2) return label;
        const timeParts = parts[1].split(':');
        if (timeParts.length < 1) return label;
        const hour = parseInt(timeParts[0]);
        
        // Format time with am/pm
        if (hour === 0) return '12am';
        if (hour === 12) return '12pm';
        if (hour < 12) return `${hour}am`;
        return `${hour - 12}pm`;
      }
      
      // Daily format: "2024-11-27" -> "Nov 27"
      const date = new Date(label);
      if (isNaN(date.getTime())) return label;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return label;
    }
  };
  
  // Format labels for x-axis
  const displayLabels = labels.map(formatLabel);

  // Create gradient fill
  const createGradient = (ctx, chartArea) => {
    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
    gradient.addColorStop(0, 'rgba(80, 70, 228, 0.0)');   // Transparent at bottom
    gradient.addColorStop(0.5, 'rgba(80, 70, 228, 0.1)'); // Light purple middle
    gradient.addColorStop(1, 'rgba(80, 70, 228, 0.3)');   // More visible at top
    return gradient;
  };

  // Chart data configuration. When comparison data is supplied, a SECOND dataset
  // is appended below the main one — slate dashed stroke, no fill, low opacity —
  // so it reads as a faded reference behind the primary indigo line.
  const datasets = [
    {
      label: label,
      data: values,
      fill: true,
      backgroundColor: (context) => {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) {
          return null;
        }
        return createGradient(ctx, chartArea);
      },
      borderColor: color,
      borderWidth: 2,
      pointRadius: 0, // Hide points by default
      pointHoverRadius: 4, // Show on hover
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
      tension: 0.4, // Smooth line curve
    },
  ];
  if (hasComparison) {
    // Plausible-style comparison line: faded SAME-color (light indigo), solid
    // stroke (not dashed), with a very subtle area fill underneath. Reads as a
    // ghost of the main series rather than a different metric. The legacy
    // dashed-slate look proved too distracting — review feedback was that it
    // read as something foreign overlaid on the chart.
    datasets.push({
      // Tooltip dataset label — used by our custom tooltip code only.
      label: `${label} (previous)`,
      data: prevValues,
      fill: true,
      backgroundColor: (context) => {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) return null;
        // Same gradient as the main series but ~⅓ the opacity so it sits
        // behind the primary fill without competing.
        const g = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        g.addColorStop(0, 'rgba(80, 70, 228, 0.0)');
        g.addColorStop(0.5, 'rgba(80, 70, 228, 0.04)');
        g.addColorStop(1, 'rgba(80, 70, 228, 0.10)');
        return g;
      },
      borderColor: 'rgba(80, 70, 228, 0.35)', // same indigo, faded
      borderWidth: 1.5,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHoverBackgroundColor: 'rgba(80, 70, 228, 0.55)',
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
      tension: 0.4,
      // Draw beneath the primary series so the current line stays the visual lead.
      order: 2,
    });
    // Ensure the primary series renders ABOVE the comparison overlay.
    datasets[0].order = 1;
  }
  const data = {
    labels: displayLabels,
    datasets,
  };

  // Chart options configuration
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false, // Hide legend
      },
      tooltip: {
        enabled: false, // Disable default tooltip
        external: (context) => {
          // Custom Plausible-style tooltip
          const { chart, tooltip } = context;
          
          // Tooltip Element
          let tooltipEl = document.getElementById('chartjs-tooltip');

          // Create element on first render
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.style.background = 'rgba(25, 30, 35, 0.95)';
            tooltipEl.style.borderRadius = '8px';
            tooltipEl.style.color = 'white';
            tooltipEl.style.opacity = 1;
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.transform = 'translate(-50%, -120%)';
            tooltipEl.style.transition = 'all .1s ease';
            tooltipEl.style.padding = '12px 16px';
            tooltipEl.style.fontSize = '14px';
            tooltipEl.style.fontFamily = 'system-ui, -apple-system, sans-serif';
            tooltipEl.style.zIndex = '1000';
            tooltipEl.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
            tooltipEl.style.minWidth = '150px';
            document.body.appendChild(tooltipEl);
          }

          // Hide if no tooltip
          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          // Set Text — Plausible-style tooltip:
          //   "UNIQUE VISITORS  ↓ 15%"           ← metric + per-point change %
          //   • Thu, 21 May          1.7k        ← current
          //   • Thu, 14 May          2k          ← previous (only when comparison)
          if (tooltip.body) {
            const dataIndex = tooltip.dataPoints[0].dataIndex;
            const originalLabel = labels[dataIndex];
            const value = tooltip.dataPoints[0].formattedValue;
            const colors = tooltip.labelColors[0];

            // Helper: render a date label (date string → "Thu, 21 May" or
            // "Thu, 21 May, 9am" depending on granularity).
            const formatDateLabel = (rawLabel) => {
              if (!rawLabel) return '';
              try {
                if (isHourlyFormat && rawLabel.includes(':')) {
                  const d = new Date(rawLabel.replace(' ', 'T'));
                  const dow = d.toLocaleDateString('en-US', { weekday: 'short' });
                  const day = d.getDate();
                  const mon = d.toLocaleDateString('en-US', { month: 'short' });
                  const h = d.getHours();
                  const hh = h === 0 ? '12am' : h === 12 ? '12pm' : h < 12 ? `${h}am` : `${h - 12}pm`;
                  return `${dow}, ${day} ${mon}, ${hh}`;
                }
                const d = new Date(rawLabel);
                const dow = d.toLocaleDateString('en-US', { weekday: 'short' });
                const day = d.getDate();
                const mon = d.toLocaleDateString('en-US', { month: 'short' });
                return `${dow}, ${day} ${mon}`;
              } catch (e) {
                return rawLabel;
              }
            };

            // Per-point change between current and prev (only computed when
            // we have a prev value to compare against).
            let changeHtml = '';
            if (hasComparison && tooltip.dataPoints.length > 1) {
              const cur = Number(tooltip.dataPoints[0].raw) || 0;
              const prev = Number(tooltip.dataPoints[1].raw) || 0;
              if (prev > 0) {
                const pct = Math.round(((cur - prev) / prev) * 100);
                const up = pct >= 0;
                // Bounce-rate convention: lower is better. For now we don't
                // invert the arrow color — matches the cards' simpler "↑ is up
                // numerically" behavior to avoid double inversion confusion.
                const arrow = up ? '↑' : '↓';
                const colorHex = up ? '#10b981' : '#ef4444';
                changeHtml = `<span style="color: ${colorHex}; font-weight: 600; font-size: 12px; margin-left: 10px;">${arrow} ${Math.abs(pct)}%</span>`;
              }
            }

            let innerHtml = '<div>';

            // Header row: METRIC NAME + change% (when comparison is on)
            innerHtml += '<div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px;">';
            innerHtml += '<span style="font-size: 11px; color: #9ca3af; font-weight: 600; letter-spacing: 0.5px;">' + label.toUpperCase() + '</span>';
            innerHtml += changeHtml;
            innerHtml += '</div>';

            // ---- Current row ----
            const formattedCur = formatDateLabel(originalLabel);
            let displayValue = value;
            if (label === 'Visit Duration') {
              const rawValue = tooltip.dataPoints[0].raw;
              displayValue = formatDuration(rawValue);
            }
            innerHtml += '<div style="display: flex; align-items: center; gap: 10px; padding: 4px 0;">';
            innerHtml += '<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ' + colors.borderColor + ';"></span>';
            innerHtml += '<span style="color: #d1d5db; font-size: 13px;">' + formattedCur + '</span>';
            innerHtml += '<span style="color: #ffffff; font-weight: 700; font-size: 14px; margin-left: auto;">' + displayValue + '</span>';
            innerHtml += '</div>';

            // ---- Previous row (only when comparison is on) ----
            // Uses the matching prev-period label so users see "Thu, 14 May"
            // beside "Thu, 21 May" — proves the day-of-week alignment worked.
            if (hasComparison && tooltip.dataPoints.length > 1) {
              const prevRawLabel = prevLabels && prevLabels[dataIndex];
              const formattedPrev = formatDateLabel(prevRawLabel);
              const prevRaw = tooltip.dataPoints[1].raw;
              const prevDisplay = label === 'Visit Duration'
                ? formatDuration(prevRaw)
                : tooltip.dataPoints[1].formattedValue;
              innerHtml += '<div style="display: flex; align-items: center; gap: 10px; padding: 4px 0;">';
              innerHtml += '<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: rgba(148, 163, 184, 0.7);"></span>';
              innerHtml += '<span style="color: #9ca3af; font-size: 13px;">' + (formattedPrev || 'Previous') + '</span>';
              innerHtml += '<span style="color: #e5e7eb; font-weight: 600; font-size: 14px; margin-left: auto;">' + prevDisplay + '</span>';
              innerHtml += '</div>';
            }

            innerHtml += '</div>';

            tooltipEl.innerHTML = innerHtml;
          }

          // Position tooltip above the point
          const position = chart.canvas.getBoundingClientRect();
          tooltipEl.style.opacity = 1;
          tooltipEl.style.left = position.left + window.pageXOffset + tooltip.caretX + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltip.caretY + 'px';
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false, // Hide x-grid lines
          drawBorder: false,
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
          },
          maxRotation: 0,
          autoSkip: true,
          autoSkipPadding: 20,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
          },
          padding: 10,
          callback: function (value) {
            // Special formatting for Visit Duration (value is in seconds)
            if (label === 'Visit Duration') {
              return formatDuration(value);
            }
            // Format large numbers (1000 -> 1k)
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'k';
            }
            return value;
          },
        },
      },
    },
  };

  // Cleanup tooltip on unmount
  useEffect(() => {
    return () => {
      const tooltipEl = document.getElementById('chartjs-tooltip');
      if (tooltipEl) {
        tooltipEl.remove();
      }
    };
  }, []);

  return (
    <div style={{ width: '100%', height: '300px' }}>
      <Line ref={chartRef} data={data} options={options} />
    </div>
  );
};

export default AreaChart;
